const STRUCTURE_KEYS = ['branches', 'branchID', 'options', 'questionTitle', 'questionType', 'questionContent', 'optionText', 'optionValue', 'optionSort'];
const TRACKING_KEYS = {
  CLIENT_ID: 'client_id',
  BRANCH_ID: 'branch_id',
  BLOCK_ID: 'block_id',
  PATH: 'path',
  EVENT: 'event',
  TITLE: 'title',
  OPTION: 'option'
};
const EVENT_KEY = 'decision_tree';
const EVENT_TYPES = {
  start: 'start',
  restart: 'restart',
  back: 'back',
  choice: 'choice',
  success: 'success',
  failure: 'failure'
};
export default class DecisionTreeController extends Classy {
  constructor() {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let structure = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super();

    // Make sure the data is an object
    if (typeof data !== 'object') throw new Error('Data must be an object');
    // Make sure the structure is an object
    if (typeof structure !== 'object') throw new Error('Structure must be an object');

    // The decision tree data
    this.data = data;
    // The progress of the decision tree
    this.progress = [this.data];

    // A unique client ID for tracking purposes
    this.clientID = this.getClientID();
    // The current branch ID
    this.branchID = 1;
    // Whether or not the decision tree has started
    this.started = false;
    // An example data object for the tracking data
    this.trackingObject = this.getExampleTrackingData();

    // Create a structure object from the array of keys
    this.structure = STRUCTURE_KEYS.reduce((obj, key) => {
      // Set the key to the value in the passed in structure object or the key itself
      obj[key] = structure[key] || key;
      return obj;
    }, {});
  }
  getClientID() {
    let id = Math.floor(Date.now() + Math.random() * Date.now()) + Math.random().toString(36).substr(2, 9);
    let idArray = id.split('');
    for (let i = idArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idArray[i], idArray[j]] = [idArray[j], idArray[i]];
    }
    return idArray.join('');
  }
  getEventTypes() {
    // Return an array of the event type values prefixed with 'decision_tree_'
    return Object.keys(EVENT_TYPES).map(key => `${EVENT_KEY}_${EVENT_TYPES[key]}`);
  }
  getExampleTrackingData() {
    return this.generateTrackingData({
      title: 'Example Title',
      option: 'Example Option',
      path: '/example/path'
    });
  }
  getCurrentProgress() {
    return {
      step: this.progress.length,
      data: this.progress[this.progress.length - 1]
    };
  }
  generateTrackingData() {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      [TRACKING_KEYS.CLIENT_ID]: this.clientID,
      [TRACKING_KEYS.BRANCH_ID]: `${this.branchID}` || '1',
      [TRACKING_KEYS.BLOCK_ID]: this.data.blockID || 'DecisionTreeBlockID',
      [TRACKING_KEYS.PATH]: data.path || window.location.pathname,
      [TRACKING_KEYS.EVENT]: EVENT_KEY + '_' + `${data.event || EVENT_TYPES.start}`,
      [TRACKING_KEYS.TITLE]: data.title || null,
      [TRACKING_KEYS.OPTION]: data.option || null,
      ...data
    };
  }

  // Renders the current state of the decision tree
  render() {
    // Grab the structure in a more readable format
    const structure = this.structure;
    // Grab the last item in the progress array
    const data = this.progress[this.progress.length - 1];
    // Grab the options from the data
    const optionsData = data[structure.options];

    // If this is the first step, track the start event
    if (this.progress.length === 1 && this.started === false) this.track(EVENT_TYPES.start);
    this.started = true;
    this.callback('render', {
      step: this.progress.length,
      type: data[structure.questionType],
      // The title of this branch
      title: data[structure.questionTitle],
      // The content of this branch
      content: data[structure.questionContent],
      // Set up the options to include a callback function which will update the progress array and re-render the decision tree
      options: optionsData.map(option => ({
        text: option[structure.optionText],
        sort: option[structure.optionSort],
        callback: () => {
          // Grab the branch from the progress array
          const branches = data[structure.branches];
          // Grab the value from the selected option
          const value = option[structure.optionValue];

          // Make sure the branch value exists
          if (!branches[value]) throw new Error(`Branch key "${value}" does not exist.`);

          // Grab this branch's ID
          this.branchID = data[structure.branchID];

          // Track the user's choice
          this.track(EVENT_TYPES.choice, {
            title: data[structure.questionTitle],
            option: option[structure.optionText]
          });

          // Add the selected option to the progress array
          this.progress.push(branches[value]);
          // Render the decision tree
          this.render();
        }
      }))
    });
  }
  track(event) {
    let data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!window.dataLayer) return;
    const trackingData = this.generateTrackingData({
      event: EVENT_KEY + '_' + `${event}`,
      ...data
    });
    window.dataLayer.push(trackingData);
    console.log('Tracking:', trackingData);
  }

  // Restarts the decision tree
  restart() {
    // Track the restart event
    this.track(EVENT_TYPES.restart);
    // Reset the progress array
    this.progress = [this.data];
    // Render the decision tree
    this.render();

    // Callback
    this.callback('restart');
  }

  // Goes back one step in the decision tree
  back() {
    // Back sure the progress has more than one item
    if (this.progress.length <= 1) return this.restart();
    // Track the back event
    this.track(EVENT_TYPES.back);
    // Remove the last item from the progress array
    this.progress.pop();
    // Render the decision tree
    this.render();

    // Callback
    this.callback('back');
  }
  success() {
    // Track the success event
    this.track(EVENT_TYPES.success, {
      title: this.progress[this.progress.length - 1][this.structure.questionTitle]
    });

    // Callback
    this.callback('success');
  }
  fail() {
    // Track the failure event
    this.track(EVENT_TYPES.failure, {
      title: this.progress[this.progress.length - 1][this.structure.questionTitle]
    });

    // Callback
    this.callback('fail');
  }
}